// ─────────────────────────────────────────────────────────────────────────────
// Homepage9 — the marketing homepage.
//
// Sections, in order:
//   Hero → 01 From Inspiration to Reality → 02 24-Hour Sourcing & Physical Samples →
//   03 All-in-One Planning → 04 Engaging Designers & Contractors → 05 Start Planning (sign-up form)
//
// Good to know:
//   • Header10 and Footer5 scroll to these section ids, so keep them:
//       "what-we-do"       From Inspiration to Reality
//       "differentiation"  24-Hour Sourcing & Physical Samples
//       "planning"         All-in-One Planning
//       "final-cta"        sign-up form
//   • Photos with tags (the hero and From Inspiration to Reality): see the notes above TaggedImage.
//   • Screen sizes: the page has TWO designs with ONE switch point, MUI's `md` (900px).
//       Mobile design  (narrower than 900px):  one column, tap-to-switch photo cards.
//       Desktop design (900px and wider):      two columns, all photo cards open.
//     So every responsive value uses only `xs` (mobile) and `md` (desktop). The Header
//     and Footer switch at the same width. Below 900px, sizes stretch smoothly
//     (e.g. with clamp) rather than adding more switch points.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import {
  GREEN, GREEN_HOVER, GREEN_FAINT, CHARCOAL, BORDER, MUTED, MUTED_FAINT,
  STONE50, labelMono, bodyFont,
} from '../../theme/tokens2';

import Header from '../components/Header10';
import Footer from '../components/Footer5';
import { apiFetch, ApiError } from '../../api/apiClient';
import { InlineAlert } from '../../shared/components/status';
import FileUpload from '../../shared/components/FileUpload';
import { normalisePhone, phoneError } from '../../shared/phone';
import PhoneInput from '../../shared/components/PhoneInput';

// Screenshots for the All-in-One Planning section (files are in src/assets)
import SPACE_PLANNING_SCREENSHOT from '../../assets/planning-space.png';
import SHORTLIST_SCREENSHOT      from '../../assets/planning-shortlist.png';
import BID_MANAGEMENT_SCREENSHOT from '../../assets/planning-bids.png';

// ─── Images ──────────────────────────────────────────────────────────────────
// Photos for the hero, From Inspiration to Reality and Samples sections (files are in src/assets)
import HERO_IMAGE    from '../../assets/Gemini_Generated_Image_pfpi1npfpi1npfpi.jpeg';
import KITCHEN_IMAGE from '../../assets/Gemini_Generated_Image_vszdx6vszdx6vszd.jpeg';
import SAMPLES_IMAGE from '../../assets/Gemini_Generated_Image_ro2m3ero2m3ero2m.jpeg';

// ─── Swatches ────────────────────────────────────────────────────────────────
// Small pictures of each material, shown in the info cards on the tagged photos
// (about 64 × 64px WebP files, saved in src/assets). Until a real swatch is added,
// the card shows a plain grey square. To add one:
//   1. Import it here, for example:
//        import OAK_VINYL_SWATCH from '../../assets/swatches/oak-vinyl.webp';
//   2. Add `swatch: OAK_VINYL_SWATCH` to that option in HERO_TAGS or KITCHEN_TAGS.
import HEROWALLPAINT_1 from '../../assets/HeroWallPaint_1.jpg';
import HEROWALLPAINT_2 from '../../assets/HeroWallPaint_2.jpg';
import HEROFEATUREWA_1 from '../../assets/HeroFeatureWa_1.jpg';
import HEROFEATUREWA_2 from '../../assets/HeroFeatureWa_2.jpg';
import HEROFEATUREWA_3 from '../../assets/HeroFeatureWa_3.jpg';
import HEROFLOORVINY_1 from '../../assets/HeroFloorViny_1.jpg';
import HEROFLOORVINY_2 from '../../assets/HeroFloorViny_2.jpg';
import HEROFLOORVINY_3 from '../../assets/HeroFloorViny_3.jpg';
import SECTION1CABIN_1 from '../../assets/Section1Cabin_1.jpg';
import SECTION1CABIN_2 from '../../assets/Section1Cabin_2.jpg';
import SECTION1CABIN_3 from '../../assets/Section1Cabin_3.jpg';
import SECTION1FLOOR_1 from '../../assets/Section1Floor_1.jpg';
import SECTION1FLOOR_2 from '../../assets/Section1Floor_2.jpg';
import SECTION1FLOOR_3 from '../../assets/Section1Floor_3.jpg';

import SECTION1BACKS_1 from '../../assets/Section1Backs_1.jpg';
import SECTION1BACKS_2 from '../../assets/Section1Backs_2.jpg';
import SECTION1BACKS_3 from '../../assets/Section1Backs_3.jpg';


// ─── Design tokens ───────────────────────────────────────────────────────────
// Built from tokens2 so the colours always match the rest of the app.
const TOKENS = {
  green:      GREEN,
  greenHover: GREEN_HOVER,
  greenFaint: GREEN_FAINT,
  charcoal:   CHARCOAL,
  border:     BORDER,
  muted:      MUTED,
  mutedFaint: MUTED_FAINT,
  stone50:    STONE50,
};

// ─── Shared style helpers ────────────────────────────────────────────────────

// Side padding for the whole page. It matches Header6 and Footer2
// (24px on mobile, 48px on desktop) so everything lines up.
const PAGE_PX = { xs: 3, md: 6 };

// The faint "blueprint" grid used behind the hero and the last section.
const gridBg = {
  backgroundImage: `
    linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)
  `,
  backgroundSize: '80px 80px',
};

// Big, light headline styles. Two sizes are used across the sections.
const headlineFullWidth = {
  ...bodyFont,
  fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
  fontWeight: 300,
  lineHeight: 1.05,
  letterSpacing: '-0.025em',
  color: TOKENS.charcoal,
  maxWidth: 800,
};
const headlineHalfWidth = {
  ...bodyFont,
  fontSize: 'clamp(2rem, 4vw, 3.4rem)',
  fontWeight: 300,
  lineHeight: 1.06,
  letterSpacing: '-0.03em',
  color: TOKENS.charcoal,
};

// ─── Keyframe injection ──────────────────────────────────────────────────────
// All animations for the page live here.
const KEYFRAMES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&display=swap');

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(80px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Material tags on the inspiration photos */
  @keyframes tagDotIn {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
    to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  @keyframes tagCardIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes tagLineIn { from { opacity: 0; } to { opacity: 0.8; } }
  @keyframes tagPulse {
    0%   { opacity: 0.6; transform: scale(1); }
    100% { opacity: 0;   transform: scale(2.4); }
  }
`;

function useStyleTag(css) {
  useEffect(() => {
    const tag = document.createElement('style');
    tag.textContent = css;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);
}

// ─── useInView hook ──────────────────────────────────────────────────────────
function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: options.margin || '-60px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

// ─── AnimatedSection ─────────────────────────────────────────────────────────
// Fades a section in when it scrolls into view.
//   id      → the section's main id (used for scrolling and the URL hash)
function AnimatedSection({ children, id, delay = 0, sx = {} }) {
  const [ref, inView] = useInView({ margin: '-60px' });
  return (
    <Box
      component="section"
      id={id}
      ref={ref}
      sx={{
        position: 'relative',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.65s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Small shared pieces ─────────────────────────────────────────────────────

// "01 | From Inspiration to Reality" style label at the top of each section.
function SectionHeader({ number, label }) {
  return (
    <Box sx={{
      borderBottom: `1px solid ${TOKENS.border}`,
      px: PAGE_PX, py: 3,
      display: 'flex', alignItems: 'center', gap: 3,
    }}>
      <Typography sx={{ ...labelMono, color: TOKENS.green }}>{number}</Typography>
      <Box sx={{ width: '1px', height: 20, backgroundColor: TOKENS.border }} />
      <Typography sx={{ ...labelMono, color: TOKENS.muted }}>{label}</Typography>
    </Box>
  );
}

// The thin line at the bottom of each section.
function SectionRule() {
  return <Box sx={{ height: '1px', width: '100%', backgroundColor: TOKENS.border }} />;
}

// Green italic words inside a headline.
function Accent({ children }) {
  return (
    <Box component="span" sx={{ fontStyle: 'italic', color: TOKENS.green }}>
      {children}
    </Box>
  );
}

// Normal grey paragraph text. Pass `sx` to change spacing or width.
function BodyText({ children, sx = {} }) {
  return (
    <Typography sx={{ ...bodyFont, fontSize: '1rem', color: TOKENS.muted, lineHeight: 1.7, ...sx }}>
      {children}
    </Typography>
  );
}

// ─── TaggedImage ─────────────────────────────────────────────────────────────
// A photo with material "tags" pinned on it.
//
// The photo is cropped to fill its box (object-fit: cover), and the box has a
// different shape on different screens — so a different part of the photo is
// cropped off each time. To keep every tag on the SAME spot of the photo, the
// tags are measured against the WHOLE photo, and the code below works out where
// that spot ends up inside the box.
//
//   tagX, tagY   → the spot on the whole photo, in % (0–100). 0,0 is the photo's top-left corner.
//   cardX, cardY → the point where the line from the dot ends, in % of the box (a layout choice).
//                  The card is pinned to this point too: `cardAnchor` says which part of the
//                  card sits on it (e.g. 'bl' = the card's bottom-left corner, 'right' = the
//                  middle of its right edge). So the line always meets the card there.
//                  Desktop only: on mobile, the card moves to the corner of the photo away
//                  from its dot (see getMobileCardPlace).
//
//   • A tag with `annotation: true` also shows a small info card, joined to its dot by a line.
//     The card lists the tag's `options` (materials from different vendors with a
//     similar look), in the order they are written, and says how many there are.
//   • Each row in a card can have a `swatch` (a small picture of the material).
//     Rows without one show a grey square. See "Swatches" near the top of the file.
//   • A tag with `annotation: false` is a dot only.
//   • Desktop (900px and wider): every card is open, and nothing is clickable.
//   • Mobile (narrower than 900px): exactly one card is open, starting with the first
//     tag that has a card. Tapping another dot (it shows a "+") switches to its card.
//     A short "Tap a dot to see options" hint shows under the photo.
//   • topLabel / bottomLabel (optional) → small dark labels on the photo's
//     top-left corner and along its bottom edge.
//   • If a tag's spot is cropped out on the current screen, that tag (and its card) is hidden.
//   • Tags appear after the photo has loaded, because we need the photo's real size.
//
// If you change a photo, update its tagX / tagY numbers so the dots land on the right things.

// Says which corner/edge of the card sits on the (cardX, cardY) point.
const ANCHOR_TRANSFORMS = {
  tl:     'translate(0, 0)',
  tr:     'translate(-100%, 0)',
  bl:     'translate(0, -100%)',
  br:     'translate(-100%, -100%)',
  top:    'translate(-50%, 0)',
  bottom: 'translate(-50%, -100%)',
  left:   'translate(0, -50%)',
  right:  'translate(-100%, -50%)',
};

// A tag is hidden when its dot would sit closer than this to the edge of the box (in px).
const TAG_EDGE_MARGIN = 12;

// Mobile only: the photo is small, so the desktop card positions (cardX / cardY)
// would cover the dots. Instead, the open card goes in the corner of the photo
// AWAY from its dot: dot in the bottom half → card at the top, dot on the right →
// card on the left, and so on. MOBILE_CARD_INSET is the gap from the photo's edge (px).
const MOBILE_CARD_INSET = 10;
function getMobileCardPlace(t, box) {
  const atTop  = t.y > box.h / 2;
  const atLeft = t.x > box.w / 2;
  const insetX = (MOBILE_CARD_INSET / box.w) * 100;
  const insetY = (MOBILE_CARD_INSET / box.h) * 100;
  return {
    cardX:      atLeft ? insetX : 100 - insetX,
    cardY:      atTop  ? insetY : 100 - insetY,
    cardAnchor: (atTop ? 't' : 'b') + (atLeft ? 'l' : 'r'),
  };
}

// Works out how the photo sits inside the box when it is cropped to fill it
// (the same as CSS object-fit: cover, centred).
// Returns the size the photo is drawn at, and how far it is shifted from the
// box's top-left corner. The shift is usually negative, because part of the
// photo hangs outside the box and is cropped off.
function getCoverFit(box, photo) {
  const scale  = Math.max(box.w / photo.w, box.h / photo.h);
  const shownW = photo.w * scale;
  const shownH = photo.h * scale;
  return { shownW, shownH, left: (box.w - shownW) / 2, top: (box.h - shownH) / 2 };
}

// ─── Swatch ──────────────────────────────────────────────────────────────────
// The small square picture of a material, on the left of each row in an info card.
// If a row has no `swatch` yet, a plain light grey square is shown instead.
// The size is fixed, so the card does not jump when the picture loads.
const SWATCH_SIZE        = 28;
const SWATCH_PLACEHOLDER = '#e7e5e4';

function Swatch({ src }) {
  const squareSx = {
    width: SWATCH_SIZE, height: SWATCH_SIZE, flexShrink: 0,
    boxSizing: 'border-box',
    border: `1px solid ${TOKENS.border}`,
    backgroundColor: SWATCH_PLACEHOLDER,
    display: 'block',
  };
  if (!src) return <Box aria-hidden sx={squareSx} />;
  // alt="" because the material's name is written right next to the swatch.
  return (
    <Box
      component="img" src={src} alt="" loading="lazy"
      width={SWATCH_SIZE} height={SWATCH_SIZE}
      sx={{ ...squareSx, objectFit: 'cover' }}
    />
  );
}

// The white plus sign shown inside a dot (on mobile) whose card is closed.
function PlusSign() {
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden style={{ display: 'block' }}>
      <path d="M4 1v6M1 4h6" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function TaggedImage({ src, alt, imageSx = {}, sx = {}, hintSx = {}, tags }) {
  const boxRef   = useRef(null);
  const photoRef = useRef(null);
  const [viewRef, inView] = useInView({ margin: '-150px' });
  const [boxSize,   setBoxSize]   = useState(null);  // { w, h } — the box, in px
  const [photoSize, setPhotoSize] = useState(null);  // { w, h } — the photo file, in px

  // Mobile design = narrower than MUI's `md` (900px), the same width where the
  // Header switches to the hamburger menu. noSsr makes the first render already correct.
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  // Mobile only: which card is open (by its label), and whether the user has
  // switched cards yet. null means "the default card".
  const [activeLabel, setActiveLabel] = useState(null);
  const [hasSwitched, setHasSwitched] = useState(false);

  // Keep boxSize up to date when the screen size changes.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setBoxSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Read the photo's real size once it has loaded.
  const readPhotoSize = () => {
    const img = photoRef.current;
    if (img && img.naturalWidth) setPhotoSize({ w: img.naturalWidth, h: img.naturalHeight });
  };
  // A photo can finish loading before React starts listening (for example when
  // it comes from the browser cache), so also check once right away.
  useEffect(readPhotoSize, [src]);

  const fit = boxSize && boxSize.w > 0 && photoSize ? getCoverFit(boxSize, photoSize) : null;

  // Work out where each dot is (in px, inside the box) and keep only the ones
  // that are still inside the box after the crop.
  // `order` and `cardOrder` are the tag's place in the original list. They set
  // the animation delay, so a tag's animation does not restart when another tag hides.
  const shownTags = fit && inView
    ? tags
        .map((t, order) => ({
          ...t,
          order,
          cardOrder: tags.filter((a) => a.annotation).indexOf(t),
          x: fit.left + (t.tagX / 100) * fit.shownW,
          y: fit.top  + (t.tagY / 100) * fit.shownH,
          // NEW: cardX / cardY are now % of the whole photo, like tagX / tagY.
          // Convert them to % of the box, because the card and line code expect that.
          cardX: ((fit.left + (t.cardX / 100) * fit.shownW) / boxSize.w) * 100,
          cardY: ((fit.top  + (t.cardY / 100) * fit.shownH) / boxSize.h) * 100,          
        }))
        .filter((t) =>
          t.x >= TAG_EDGE_MARGIN && t.x <= boxSize.w - TAG_EDGE_MARGIN &&
          t.y >= TAG_EDGE_MARGIN && t.y <= boxSize.h - TAG_EDGE_MARGIN)
    : [];
  const shownCards = shownTags.filter((t) => t.annotation);

  // Mobile: exactly one card is open. It is the one the user tapped, or else the
  // first visible tag that has a card. (shownCards keeps the list order, so if the
  // first tag is cropped off, the next visible one opens instead.)
  const activeCard = shownCards.find((t) => t.label === activeLabel) || shownCards[0] || null;
  // Desktop shows every card; mobile shows only the open one, moved to the far corner.
  const openCards  = isMobile
    ? (activeCard ? [{ ...activeCard, ...getMobileCardPlace(activeCard, boxSize) }] : [])
    : shownCards;

  // Mobile: tapping a closed dot opens its card. Tapping the open dot does nothing.
  const openCard = (t) => {
    if (!activeCard || t.label === activeCard.label) return;
    setActiveLabel(t.label);
    setHasSwitched(true);
  };

  // The first cards fade in slowly after the page loads. After the user switches
  // cards, the new card and its line fade in quickly, so the tap feels responsive.
  const lineAnimation = (t) => hasSwitched
    ? 'tagLineIn 0.2s ease 0s forwards'
    : `tagLineIn 0.5s ease ${0.8 + t.cardOrder * 0.12}s forwards`;
  const cardAnimation = (t) => hasSwitched
    ? 'tagCardIn 0.2s ease 0s forwards'
    : `tagCardIn 0.5s ease ${0.9 + t.cardOrder * 0.12}s forwards`;

  // Dots are a little bigger on mobile so the plus sign is easy to see.
  const dotSize = isMobile ? 18 : 14;

  return (
    <Box ref={viewRef}>
      <Box ref={boxRef} sx={{ position: 'relative', overflow: 'hidden', ...sx }}>
        <Box
          component="img"
          ref={photoRef}
          onLoad={readPhotoSize}
          src={src}
          alt={alt}
          sx={{ width: '100%', display: 'block', objectFit: 'cover', ...imageSx }}
        />

        {/* Lines from each dot to its card. The SVG uses % coordinates, and
            non-scaling-stroke keeps every line 1px thin at any image size. */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
        >
          {openCards.map((t) => (
            <line
              key={t.label}
              x1={(t.x / boxSize.w) * 100}
              y1={(t.y / boxSize.h) * 100}
              x2={t.cardX}
              y2={t.cardY}
              stroke="#fff"
              strokeWidth="1.5"
              strokeOpacity="0.8"
              vectorEffect="non-scaling-stroke"
              style={{ opacity: 0, animation: lineAnimation(t) }}
            />
          ))}
        </svg>

        {/* Tag dots. On mobile, dots that have a card are real buttons. */}
        {shownTags.map((t) => {
          const tappable = isMobile && t.annotation;
          const isActive = tappable && activeCard?.label === t.label;
          return (
            <Box
              key={t.label}
              component={tappable ? 'button' : 'div'}
              type={tappable ? 'button' : undefined}
              onClick={tappable ? () => openCard(t) : undefined}
              aria-label={tappable ? `Show ${t.label.toLowerCase()} options` : undefined}
              aria-pressed={tappable ? isActive : undefined}
              // left/top change every time the screen is resized, so they are set as a
              // plain `style` (not `sx`). That avoids creating a new CSS class for every size.
              style={{ left: t.x, top: t.y }}
              sx={{
                position: 'absolute',
                transform: 'translate(-50%, -50%)',
                // Dots sit under the cards (cards use zIndex 5). If a card covers a dot,
                // the dot is hidden — that is fine. On mobile this also means a covered dot
                // can't be tapped, so its card may never open on small phones. This is a
                // deliberate choice: a clean photo matters more than showing every card.
                zIndex: 4,
                opacity: 0,
                animation: `tagDotIn 0.4s ease ${0.5 + t.order * 0.1}s forwards`,
                // Mobile: an invisible 44 × 44px tap area, centred on the dot.
                ...(tappable && {
                  width: 44, height: 44, p: 0, m: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'none', border: 'none', borderRadius: '50%',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: '-6px' },
                }),
              }}
            >
              <Box sx={{ position: 'relative', width: dotSize, height: dotSize }}>
                {/* pulsing ring */}
                <Box
                  component="span"
                  sx={{
                    position: 'absolute', inset: '-3px',
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `2px solid ${TOKENS.green}`,
                    animation: 'tagPulse 2.4s ease-out infinite',
                    opacity: 0,
                  }}
                />
                {/* solid dot (with a plus sign when its card is closed, on mobile) */}
                <Box
                  component="span"
                  sx={{
                    position: 'relative',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: dotSize, height: dotSize,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    backgroundColor: TOKENS.green,
                    border: '2px solid #fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                  }}
                >
                  {tappable && !isActive && <PlusSign />}
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Info cards (only for tags with annotation: true) */}
        {openCards.map((t) => (
          <Box
            key={t.label}
            sx={{
              position: 'absolute',
              left: `${t.cardX}%`,
              top: `${t.cardY}%`,
              transform: ANCHOR_TRANSFORMS[t.cardAnchor] || 'translate(0, 0)',
              zIndex: 5,
              cursor: 'default',
              opacity: 0,
              animation: cardAnimation(t),
            }}
          >
            <Box sx={{
              boxSizing: 'border-box',
              border: `1px solid ${TOKENS.border}`,
              backgroundColor: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(6px)',
              padding: '8px 10px',
              minWidth: 200,
              maxWidth: 240,
              boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
            }}>
              {/* Top row: the material type, and how many options there are */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography component="span" sx={{ ...labelMono, color: TOKENS.green, fontSize: '8px' }}>
                  {t.label}
                </Typography>
                {t.options.length > 1 && (
                  <Typography component="span" sx={{ ...labelMono, color: TOKENS.green, fontSize: '8px', whiteSpace: 'nowrap' }}>
                    {t.options.length} OPTIONS
                  </Typography>
                )}
              </Box>

              {/* One row per option. Each row: swatch → name and vendor → price.
                  A dashed line separates the rows. */}
              <Box sx={{ mt: '6px', pt: t.options.length > 1 ? '5px' : '2px' }}>
                {t.options.map((o, j) => (
                  <Box
                    key={o.name}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      mt: j === 0 ? 0 : '5px',
                      pt: j === 0 ? 0 : '5px',
                      borderTop: j === 0 ? 'none' : `1px dashed ${TOKENS.border}`,
                    }}
                  >
                    <Swatch src={o.swatch} />
                    {/* minWidth: 0 lets long names shorten with "…" instead of stretching the card */}
                    <Box sx={{ flex: 1, minWidth: 0, mr: '2px' }}>
                      <Typography sx={{
                        ...bodyFont, fontSize: '0.7rem', color: TOKENS.charcoal,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {o.name}
                      </Typography>
                      <Typography sx={{
                        ...bodyFont, fontSize: '0.65rem', color: TOKENS.muted,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {o.vendor}
                      </Typography>
                    </Box>
                    <Typography component="span" sx={{ ...labelMono, color: TOKENS.muted, fontSize: '9px', whiteSpace: 'nowrap' }}>
                      {o.price}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Mobile only: a short hint under the photo */}
      {isMobile && (
        <Typography sx={{ ...labelMono, color: TOKENS.mutedFaint, fontSize: '9px', mt: 1.5, ...hintSx }}>
          Tap a dot to see options
        </Typography>
      )}
    </Box>
  );
}

// ─── Hero Section ────────────────────────────────────────────────────────────

// Materials tagged on the hero image. 
// tagX / tagY = the spot on the WHOLE photo, in %.  cardX / cardY = where the card sits in the box.
// `options` = materials from different vendors with a similar look, all listed in the card.
// If you change this photo, update the tagX / tagY numbers.

const HERO_TAGS = [
  {
    label: 'WALL PAINT',
    tagX: 30, tagY: 25, cardX: 30, cardY: 40, cardAnchor: 'top', annotation: true,
    options: [
      { name: 'Snow Flakes',   vendor: 'Chatswood Paints',   price: '$26/L', swatch: HEROWALLPAINT_1 },
      { name: 'Sneaker White', vendor: 'Queensway Paints', price: '$26/L', swatch: HEROWALLPAINT_2 },
    ],
  },
  {
    label: 'FEATURE WALL LAMINATE',
    tagX: 65, tagY: 37, cardX: 65, cardY: 35, cardAnchor: 'bottom', annotation: true,
    options: [
      { name: 'Olive Ash Sienna', vendor: 'Surry Hills Surfaces',  price: '$60/pc', swatch: HEROFEATUREWA_1 },
      { name: 'African Ash',      vendor: 'Mosman Laminates', price: '$59/pc', swatch: HEROFEATUREWA_2 },
      { name: 'American Ash',     vendor: 'Nine Elms (Sloane Sq.)',             price: '$59/pc', swatch: HEROFEATUREWA_3 },
    ],
  },
  {
    label: 'FLOOR VINYL',
    tagX: 75, tagY: 80, cardX: 65, cardY: 80, cardAnchor: 'right', annotation: true,
    options: [
      { name: 'Autumn Chestnut', vendor: 'Finsbury Floors',    price: '$6.80/sf', swatch: HEROFLOORVINY_1 },
      { name: 'Willow Oak',      vendor: 'Wood Original',      price: '$7.20/sf', swatch: HEROFLOORVINY_2 },
      { name: 'Natural Hickory', vendor: 'Penrith Floorworks', price: '$7.70/sf', swatch: HEROFLOORVINY_3 },
    ],
  },
];

function HeroSection() {
  const [btnHover, setBtnHover] = useState(false);
  const scrollToCTA = () =>
    document.getElementById('final-cta')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        // The hero is only as tall as its content on every screen (it does not fill the
        // screen). So tall screens get no big empty gaps, and the next section peeks in
        // below the hero, inviting people to scroll.
        overflow: 'hidden',
        ...bodyFont,
        ...gridBg,
      }}
    >
      <Box sx={{
        maxWidth: 1400, mx: 'auto',
        boxSizing: 'border-box',
        px: PAGE_PX,
        // Top: the 64px header + 32px of space, the same on every screen, so the
        // headline never touches the header.
        pt: 12,
        pb: { xs: 12, md: 8 },
      }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
          gap: { xs: 6, md: 8 },
          alignItems: 'center',
        }}>

          {/* Left: copy (above the image on mobile, so the headline
              and the button are the first things people see) */}
          <Box>
            <Typography
              component="h1"
              sx={{
                ...bodyFont,
                fontSize: 'clamp(2.4rem, 5vw, 4.5rem)',
                fontWeight: 300,
                lineHeight: 1.05,
                letterSpacing: '-0.03em',
                color: TOKENS.charcoal,
                opacity: 0,
                animation: 'fadeUp 0.9s cubic-bezier(0.22,1,0.36,1) 0.3s forwards',
              }}
            >
              Shape your home with{' '}
              <Accent>Savvy confidence.</Accent>
            </Typography>

            <Typography sx={{
              ...bodyFont,
              mt: 3,
              fontSize: '1.05rem',
              lineHeight: 1.65,
              color: TOKENS.muted,
              maxWidth: 460,
              opacity: 0,
              animation: 'fadeInUp 0.7s ease 0.6s forwards',
            }}>
              Share the ideas that you’ve saved on social media or generated with AI. We 
              find real materials that match the look, help you plan each room, and connect 
              you with renovation firms who can bring your vision to life.
            </Typography>

            {/* CTA */}
            <Box sx={{ mt: 4, opacity: 0, animation: 'fadeInUp 0.5s ease 0.9s forwards' }}>
              <Button
                onClick={scrollToCTA}
                onMouseEnter={() => setBtnHover(true)}
                onMouseLeave={() => setBtnHover(false)}
                disableRipple
                endIcon={
                  <ArrowForwardIcon sx={{
                    fontSize: '1rem !important',
                    transition: 'transform 0.2s ease',
                    transform: btnHover ? 'translateX(4px)' : 'translateX(0)',
                  }} />
                }
                sx={{
                  backgroundColor: TOKENS.green, color: '#fff',
                  px: '28px', py: '14px',
                  fontSize: '0.975rem', fontWeight: 500, letterSpacing: '0.05em',
                  textTransform: 'none', borderRadius: 0,
                  ...bodyFont,
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: TOKENS.greenHover },
                }}
              >
                Get Started
              </Button>
            </Box>
          </Box>

          {/* Right: tagged inspiration image (below the copy on mobile) */}
          <Box sx={{ opacity: 0, animation: 'fadeIn 1s ease 0.5s forwards' }}>
            <TaggedImage
              src={HERO_IMAGE}
              alt="Singapore HDB living room renovation inspiration, warm minimalist Japandi style"
              // Mobile: grows with the screen width, from 340px (phones) up to 560px.
              imageSx={{ height: { xs: 'clamp(340px, 60vw, 560px)', md: 540 } }}
              sx={{ border: `1px solid ${TOKENS.border}` }}
              tags={HERO_TAGS}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── 01 · From Inspiration to Reality ────────────────────────────────────────

// Materials tagged on the kitchen image. Three have info cards; two are dots only.
// tagX / tagY = the spot on the WHOLE photo, in %.  cardX / cardY = where the card sits in the box.
// `options` = materials from different vendors with a similar look, all listed in the card.
const KITCHEN_TAGS = [
  {
    label: 'FLOORING',
    tagX: 50, tagY: 73, cardX: 40, cardY: 73, cardAnchor: 'right', annotation: true,
    options: [
      { name: 'Tuscan Clay',    vendor: 'Balmain Floorworks',   price: '$4.60/sqft', swatch: SECTION1FLOOR_1 },
      { name: 'Cotto Rosso',    vendor: 'Ealing & Co. Tiles',   price: '$4.90/sqft', swatch: SECTION1FLOOR_2 },
      { name: 'Sunbaked Earth', vendor: 'Sandstone City Tiles', price: '$5.50/sqft', swatch: SECTION1FLOOR_3 },
    ],
  },
  {
    label: 'CABINET LAMINATE',
    tagX: 43, tagY: 37, cardX: 40, cardY: 37, cardAnchor: 'right', annotation: true,
    options: [
      { name: 'Gunsmoke',   vendor: 'Parramatta Panel Co.', price: '$54/pc', swatch: SECTION1CABIN_1 },
      { name: 'Cool Grey',  vendor: 'Edgware Laminates',    price: '$48/pc', swatch: SECTION1CABIN_2 },
      { name: 'Pearl Grey', vendor: 'Surry Hills Surfaces', price: '$54/pc', swatch: SECTION1CABIN_3 },
    ],
  },
  {
    label: 'BACKSPLASH',
    tagX: 51, tagY: 51, cardX: 67, cardY: 51, cardAnchor: 'left', annotation: true,
    options: [
      { name: 'Metro White',   vendor: 'Sandstone City Tiles', price: '$4.90/sqft', swatch: SECTION1BACKS_3 },
      { name: 'Alabaster',     vendor: 'Lygon Street Tiles',   price: '$5.20/sqft', swatch: SECTION1BACKS_2 },
      { name: 'Minimal Ivory', vendor: 'Caledonian Ceramics',     price: '$5.60/sqft', swatch: SECTION1BACKS_1 },
    ],
  },
];

function MaterialSourcingSection() {
  return (
    // "what-we-do" is an older id for this section. No link uses it now, but it is kept
    // so that any old shared links (…/#what-we-do) still work.
    <AnimatedSection
      sx={{ backgroundColor: TOKENS.stone50, overflow: 'hidden' }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <SectionHeader number="01" label="From Inspiration to Reality" />

        {/* Intro */}
        <Box sx={{ px: PAGE_PX, py: { xs: 6, md: 8 }, borderBottom: `1px solid ${TOKENS.border}` }}>
          <Typography component="h2" sx={headlineFullWidth}>
            Let's turn your inspiration into{' '}
            <Accent>real possibilities.</Accent>
          </Typography>
          <BodyText sx={{ mt: 2, maxWidth: 620 }}>
            Found an interior you can’t stop thinking about? We’ll match every surface in it to 
            real materials in the market — from tiles and vinyl to laminates and paint.
          </BodyText>
          <BodyText sx={{ mt: '12px', maxWidth: 620 }}>
            See what’s available, and at what price, before meeting any renovation firm. Your inspiration 
            becomes more than a picture, with real possibilities to compare and choose from.
          </BodyText>
        </Box>

        {/* Tagged inspiration image — materials pinned on the photo with cards */}
        <TaggedImage
          src={KITCHEN_IMAGE}
          alt="Singapore HDB kitchen renovation inspiration"
          // Mobile: grows with the screen width, from 360px (phones) up to 560px.
          imageSx={{ height: { xs: 'clamp(360px, 60vw, 560px)', md: 540 } }}
          tags={KITCHEN_TAGS}
          hintSx={{ px: PAGE_PX, pb: 2 }}
        />
      </Box>
      <SectionRule />
    </AnimatedSection>
  );
}

// ─── 02 · 24-Hour Sourcing & Physical Samples ────────────────────────────────

function SamplesSection() {
  return (
    // "differentiation" is the id that the Footer "24-Hour Sourcing & Physical Samples" link scrolls to.
    <AnimatedSection
      id="sourcing"
      sx={{ backgroundColor: '#fff', overflow: 'hidden' }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <SectionHeader number="02" label="24-Hour Sourcing & Physical Samples" />

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        }}>
          {/* Left: copy */}
          <Box sx={{
            px: PAGE_PX, py: { xs: 6, md: 8 },
            borderRight:  { xs: 'none', md: `1px solid ${TOKENS.border}` },
            borderBottom: { xs: `1px solid ${TOKENS.border}`, md: 'none' },
          }}>
            <Typography component="h2" sx={headlineHalfWidth}>
              Bring the{' '}
              <Accent>Showroom home.</Accent>
            </Typography>

            <Box sx={{ height: '1px', width: 48, backgroundColor: TOKENS.green, mt: 3, mb: 3 }} />

            <BodyText sx={{ maxWidth: 440 }}>
              Colours on a screen are never quite right. We send you a shortlist within 24 hours, and when 
              samples are available, we deliver them to your door. No more weekends spent going from 
              showroom to showroom.
            </BodyText>
            <BodyText sx={{ mt: 2, maxWidth: 440 }}>
              See how the floor tile looks next to the cabinet laminate. With so many choices, seeing 
              them side by side makes deciding easier. Your shortlist also gives renovation firms a 
              clearer idea of the style you are after.
            </BodyText>
          </Box>

          {/* Right: photo of the samples, with a "delivered" label */}
          <Box sx={{ position: 'relative', overflow: 'hidden', minHeight: 400 }}>
            <Box
              component="img"
              src={SAMPLES_IMAGE}
              alt="Physical renovation material samples on a dining table in a Singapore HDB home"
              sx={{
                width: '100%',
                height: { xs: 300, md: '100%' },
                minHeight: 400,
                display: 'block',
                objectFit: 'cover',
              }}
            />
            <Box aria-hidden sx={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.25), transparent 50%, rgba(0,0,0,0.05))',
            }} />

            {/* Floating label — delivered */}
            <Box sx={{
              position: 'absolute', bottom: 24, right: 24,
              border: '1px solid rgba(255,255,255,0.60)',
              backgroundColor: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(4px)',
              padding: '10px 14px',
            }}>
              <Typography sx={{ ...labelMono, color: TOKENS.green, fontSize: '9px' }}>DELIVERED TO YOUR DOOR</Typography>
              <Typography sx={{ ...bodyFont, fontSize: '0.75rem', color: '#fff', mt: '2px' }}>
                Tiles · Laminate · Paint cards
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <SectionRule />
    </AnimatedSection>
  );
}

// ─── 03 · All-in-One Planning ────────────────────────────────────────────────

// Three features shown as a zig-zag: the screenshot is on the left for the
// 1st and 3rd rows, and on the right for the 2nd.
// Each feature has a small label (tag), a headline, and a short paragraph (body).
const PLANNING_FEATURES = [
  {
    tag: 'SPACE PLANNING',
    headline: 'We explore different ways to use each room, so you don\u2019t have to start from scratch.',
    body: 'Compare the options side by side before any work begins.',
    image: SPACE_PLANNING_SCREENSHOT,
    alt: 'Space planning screen showing the floor plan and layout options for a room',
  },
  {
    tag: 'MATERIAL AND FURNITURE SHORTLISTS',
    headline: 'Your shortlist, room by room, with vendors and prices in one place.',
    body: 'Rank your favourites as you go, so choosing feels easy instead of overwhelming.',
    image: SHORTLIST_SCREENSHOT,
    alt: 'Shortlist screen showing chosen materials and furniture room by room, with vendors and prices',
  },
  {
    tag: 'COMPARE PROPOSALS',
    headline: 'Compare proposals from renovation firms side by side, with full cost breakdowns.',
    body: 'Sort by price or Google rating, and see which firm is lowest cost or highest rated at a glance.',
    image: BID_MANAGEMENT_SCREENSHOT,
    alt: 'Bid management screen showing proposals from several firms side by side, with costs and ratings',
  },
];

// One feature row: the copy next to a large screenshot. There are no lines here;
// space and the screenshot's shadow do the separating.
// On desktop the screenshot alternates sides (left, right, left) and takes about
// 58% of the row. On mobile the copy always comes first, then the screenshot.
function PlanningModule({ feature, index }) {
  const screenshotLeft = index % 2 === 0;

  return (
    <Box sx={{ px: PAGE_PX, py: { xs: 6, md: 10 } }}>
      <Box sx={{
        display: 'grid',
        alignItems: 'center',
        gap: { xs: 4, md: 8 },
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: screenshotLeft ? 'minmax(0, 7fr) minmax(0, 5fr)' : 'minmax(0, 5fr) minmax(0, 7fr)',
        },
      }}>
        {/* Copy. It comes first in the code, so it is first on mobile (and for screen readers).
            On desktop, `order` puts it on the correct side. */}
        <Box sx={{ order: { md: screenshotLeft ? 2 : 1 } }}>
          <Typography sx={{ ...labelMono, color: TOKENS.green, fontSize: '0.7rem' }}>{feature.tag}</Typography>
          <Typography
            component="h3"
            sx={{
              ...bodyFont,
              mt: 2,
              fontSize: 'clamp(1.5rem, 2.4vw, 2.1rem)',
              fontWeight: 300,
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              color: TOKENS.charcoal,
              maxWidth: 480,
            }}
          >
            {feature.headline}
          </Typography>
          <BodyText sx={{ mt: 2, maxWidth: 440 }}>{feature.body}</BodyText>
        </Box>

        {/* Screenshot: a soft shadow instead of a frame */}
        <Box
          component="img"
          src={feature.image}
          alt={feature.alt}
          sx={{
            order: { md: screenshotLeft ? 1 : 2 },
            display: 'block',
            width: '100%',
            maxWidth: 760,       // only matters on wide mobile screens (the row is stacked); on desktop the column is narrower
            height: 'auto',      // the screenshot keeps its own shape and is never cropped
            // Keeps a 4:3 space reserved until the screenshot has loaded, so the page does not jump.
            aspectRatio: 'auto 4 / 3', borderRadius: '12px',
            boxShadow: '0 20px 50px -12px rgba(0,0,0,0.20), 0 6px 16px -6px rgba(0,0,0,0.10)',
          }}
        />
      </Box>
    </Box>
  );
}

function PlanningSection() {
  return (
    <AnimatedSection
      id="planning"
      sx={{ backgroundColor: TOKENS.stone50, overflow: 'hidden' }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <SectionHeader number="03" label="All-in-One Planning" />

        {/* Intro (the first row's top padding gives the space below it) */}
        <Box sx={{ px: PAGE_PX, pt: { xs: 6, md: 8 } }}>
          <Typography component="h2" sx={headlineFullWidth}>
            Plan your space, define your style,{' '}
            <Accent>bring it all together.</Accent>
          </Typography>
          <BodyText sx={{ mt: 2, maxWidth: 620 }}>
            Your renovation plan brings together the look you love and layouts that suit the 
            way you live, giving you the full picture of your future home.
          </BodyText>
          <BodyText sx={{ mt: '12px', maxWidth: 620 }}>
            This becomes your renovation brief, a clear summary of what you want. Share it 
            with the designers of your choice and get proposals that match your vision.
          </BodyText>
        </Box>

        {/* Feature rows — screenshot left / right / left */}
        {PLANNING_FEATURES.map((feature, i) => (
          <PlanningModule key={feature.tag} feature={feature} index={i} />
        ))}
      </Box>
      <SectionRule />
    </AnimatedSection>
  );
}

// ─── 04 · Engaging Designers & Contractors ───────────────────────────────────

// Flow: homeowner information → Savvy Renovator → designers / contractors → proposals
const FLOW_STEPS = [
  { tag: 'YOUR INFORMATION',        desc: 'Floor plan, space planning, lifestyle questionnaire, inspiration and materials.' },
  { tag: 'SAVVY RENOVATOR',         desc: 'We compile your information into a clearer picture of what you\u2019re after.' },
  { tag: 'DESIGNERS & CONTRACTORS', desc: 'With your permission, we share it with the professionals we connect you with.' },
  { tag: 'PROPOSALS',               desc: 'Compare the proposals you receive — without repeating yourself every time.' },
];

function FlowStep({ step, index, isLast }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
      {/* Number box + the line down to the next step */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{
          width: 32, height: 32,
          boxSizing: 'border-box',
          border: `1px solid ${TOKENS.greenFaint}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography component="span" sx={{ ...labelMono, color: TOKENS.green, fontSize: '9px' }}>
            {String(index + 1).padStart(2, '0')}
          </Typography>
        </Box>
        {!isLast && <Box sx={{ width: '1px', height: 48, backgroundColor: TOKENS.border, mt: '4px' }} />}
      </Box>

      <Box sx={{ pb: isLast ? 0 : 4 }}>
        <Typography sx={{ ...labelMono, color: TOKENS.green, fontSize: '0.7rem' }}>{step.tag}</Typography>
        <Typography sx={{
          ...bodyFont, fontSize: '0.95rem', color: TOKENS.muted, lineHeight: 1.6, mt: '6px', maxWidth: 380,
        }}>
          {step.desc}
        </Typography>
      </Box>
    </Box>
  );
}

function ProfessionalsSection() {
  return (
    <AnimatedSection
      sx={{ backgroundColor: '#fff', overflow: 'hidden' }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <SectionHeader number="04" label="Engaging Designers & Contractors" />

        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
        }}>
          {/* Left: copy */}
          <Box sx={{
            px: PAGE_PX, py: { xs: 6, md: 8 },
            borderRight:  { xs: 'none', md: `1px solid ${TOKENS.border}` },
            borderBottom: { xs: `1px solid ${TOKENS.border}`, md: 'none' },
          }}>
            <Typography component="h2" sx={headlineHalfWidth}>
              Give your designer or contractor a{' '}
              <Accent>clearer picture</Accent>{' '}
              from the start.
            </Typography>

            <Box sx={{ height: '1px', width: 48, backgroundColor: TOKENS.green, mt: 3, mb: 3 }} />

            <BodyText sx={{ maxWidth: 440 }}>
                As you plan, Savvy Renovator builds a clear picture of your home, your lifestyle, your
                layout preferences and the materials you're considering.
            </BodyText>
            <BodyText sx={{ mt: 2, maxWidth: 440 }}>
                With your permission, we share this with the designers and contractors we connect you with
                before you meet, so they come prepared and you don't have to repeat yourself.
            </BodyText>
            <BodyText sx={{ mt: 2, maxWidth: 440 }}>
                Once the proposals come in, we'll help you compare them side by side.
            </BodyText>
            
          </Box>

          {/* Right: flow visual */}
          <Box sx={{ px: PAGE_PX, py: { xs: 6, md: 8 }, backgroundColor: TOKENS.stone50 }}>
            <Typography sx={{ ...labelMono, color: TOKENS.muted, fontSize: '0.7rem', mb: 4 }}>
              HOW IT WORKS
            </Typography>
            <Box>
              {FLOW_STEPS.map((step, i) => (
                <FlowStep key={step.tag} step={step} index={i} isLast={i === FLOW_STEPS.length - 1} />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
      <SectionRule />
    </AnimatedSection>
  );
}

// ─── 05 · Final CTA (sign-up form) ───────────────────────────────────────────
// On submit, the form sends everything in one request: the login details (phone
// number) and the household profile together. See handleSubmit below.

// ── FormField wrapper ──
function FormField({ label, children }) {
  return (
    <Box>
      <Typography sx={{ ...labelMono, color: TOKENS.muted, fontSize: '9px', display: 'block', mb: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

// ── Select Fields (Property Type, and Property Status) ──
function SelectField({ label, options, value, onChange }) {
  const [focused, setFocused] = useState(false);
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component="select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        sx={{
          width: '100%',
          appearance: 'none',
          border: `1px solid ${focused ? TOKENS.green : TOKENS.border}`,
          backgroundColor: '#fff',
          px: 2, py: '14px',
          fontSize: '0.875rem', color: TOKENS.charcoal,
          ...bodyFont,
          outline: 'none',
          cursor: 'pointer',
          transition: 'border-color 0.2s ease',
          pr: '40px',
        }}
      >
        <option value="">{label}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </Box>
      {/* Chevron */}
      <Box sx={{
        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
        pointerEvents: 'none', color: TOKENS.muted, display: 'flex',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </Box>
    </Box>
  );
}

// ── Styles for the text inputs (Desired Renovation Start Date) ──
const inputSx = (focused) => ({
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${focused ? TOKENS.green : TOKENS.border}`,
  backgroundColor: '#fff',
  px: 2, py: '14px',
  fontSize: '0.875rem', color: TOKENS.charcoal,
  ...bodyFont,
  outline: 'none',
  transition: 'border-color 0.2s ease',
  '&::placeholder': { color: 'rgba(0,0,0,0.25)' },
});

function FinalCTASection() {
  const [formData, setFormData] = useState({
    phonenumber:     '', startdate:       '',
    property_type:   '', property_status: '',
    floorplan_url:   '',
  });
  const [dateFocused,   setDateFocused]   = useState(false);
  const [submitted,     setSubmitted]     = useState(false);
  const [submitting,    setSubmitting]    = useState(false);
  const [submitHovered, setSubmitHovered] = useState(false);
  const [submitError,   setSubmitError]   = useState('');

  // ── Field change handler ──────────────────────────────────────────────────────
  // Single handler for all text/select fields; keyed by the formData field name.
  const handleChange = (field) => (value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // ── Submit handler ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {

    // Clear any error left over from a previous attempt.
    setSubmitError('');

    // Validation: check all fields are filled.
    if (
      !formData.phonenumber     ||
      !formData.startdate       ||
      !formData.property_type   ||
      !formData.property_status ||
      !formData.floorplan_url
    ) {
      setSubmitError('Please fill in all fields and upload your floor plan before submitting.');
      return;
    }

    // Validation: a Singapore mobile number (8 digits, starting with 8 or 9).
    const phoneProblem = phoneError(formData.phonenumber);
    if (phoneProblem) {
      setSubmitError(phoneProblem);
      return;
    }
    // Clean it ("+65 9123 4567" becomes "91234567") so sign-in always matches.
    const phone = normalisePhone(formData.phonenumber);

    // Everything is sent in ONE request, in two groups:
    //   loginCredentials → creates the user account keyed to their phone number.
    //                      The phone number doubles as both the identifier and the initial password.
    //   householdProfile → the full formData object, which includes floorplan_url set by
    //                      FileUpload after the GCS upload completes.
    // The backend saves both together, so we can never end up with an account but no profile.
    const registration = {
      loginCredentials: {
        phonenumber:  phone,
        passwordHash: phone,
      },
      householdProfile: { ...formData, phonenumber: phone },
    };

    setSubmitting(true);
    try {
      await apiFetch('/api/registration', { method: 'POST', auth: false, body: registration });
      setSubmitted(true);
    } catch (err) {
      // 409 = this phone number already has an account.
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError('This phone number is already registered. Please sign in instead.');
      } else {
        setSubmitError(err instanceof ApiError ? err.userMessage : 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatedSection id="final-cta" sx={{ overflow: 'hidden', ...gridBg }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <SectionHeader number="05" label="Start Planning" />

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' } }}>

          {/* Left: headline and short copy */}
          <Box sx={{
            borderRight:  { xs: 'none', md: `1px solid ${TOKENS.border}` },
            borderBottom: { xs: `1px solid ${TOKENS.border}`, md: 'none' },
            px: PAGE_PX, py: 8,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <Typography
              component="h2"
              sx={{
                ...bodyFont,
                fontSize: 'clamp(2.2rem, 4vw, 4rem)',
                fontWeight: 300, lineHeight: 1.04, letterSpacing: '-0.03em',
                color: TOKENS.charcoal,
              }}
            >
              See what's{' '}
              <Accent>possible for your home.</Accent>
            </Typography>

            <BodyText sx={{ mt: 3, fontSize: '1.1rem', lineHeight: 1.65 }}>
              Upload your floor plan and start exploring the possibilities for your home — from layouts and
              materials to the information you'll eventually share with your designer or contractor.
            </BodyText>


          </Box>

          {/* Right: Form or Success state (white panel so the form stays easy to read over the grid) */}
          <Box sx={{
            backgroundColor: '#fff',
            px: PAGE_PX, py: 8,
            display: 'flex', flexDirection: 'column',
          }}>
            {submitted ? (
              // ── Success state ──
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, py: 4 }}>
                {/* Green tick */}
                <Box sx={{
                  width: 40, height: 40, border: `1px solid ${TOKENS.green}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 5,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={TOKENS.green} strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Box>

                <Typography sx={{
                  ...bodyFont,
                  fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
                  fontWeight: 300, lineHeight: 1.1, letterSpacing: '-0.025em',
                  color: TOKENS.charcoal, mb: 3,
                }}>
                  You're on your way to a<br />
                  <Accent>smarter renovation.</Accent>
                </Typography>

                <BodyText sx={{ maxWidth: 380 }}>
                  We've received your details and will reach out via WhatsApp shortly. In the meantime, sit back — clarity is coming.
                </BodyText>
              </Box>
            ) : (
              // ── Form state ──
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Phone */}
                <FormField label="PHONE NUMBER">
                  <PhoneInput
                    value={formData.phonenumber}
                    onChange={handleChange('phonenumber')}
                    ariaLabel="Phone number"
                    padding="14px 16px"
                    fontSize="0.875rem"
                  />
                </FormField>
                {/* Start date */}
                <FormField label="DESIRED RENOVATION START DATE">
                  <Box
                    component="input"
                    type="month"
                    value={formData.startdate}
                    onChange={(e) => handleChange('startdate')(e.target.value)}
                    onFocus={() => setDateFocused(true)}
                    onBlur={() => setDateFocused(false)}
                    sx={inputSx(dateFocused)}
                  />
                </FormField>

                {/* Property type */}
                <FormField label="PROPERTY TYPE">
                  <SelectField
                    label="HDB or Condo?"
                    options={['HDB', 'Condo']}
                    value={formData.property_type}
                    onChange={handleChange('property_type')}
                  />
                  <Typography sx={{
                    ...bodyFont,
                    fontSize: '0.8rem',
                    color: TOKENS.mutedFaint,
                    letterSpacing: '0.02em',
                    mt: 0.75,
                    lineHeight: 1.5,
                  }}>
                    Our tools are currently optimised for single-storey HDB and Condo. More property types coming soon!
                  </Typography>
                </FormField>

                {/* Property status */}
                <FormField label="PROPERTY STATUS">
                  <SelectField
                    label="New, Resale, or Existing?"
                    options={['New', 'Resale', 'Existing']}
                    value={formData.property_status}
                    onChange={handleChange('property_status')}
                  />
                </FormField>

                {/* Floor plan upload */}
                <FormField label="FLOOR PLAN">
                  <FileUpload
                    onFileUpload={handleChange('floorplan_url')}
                    bucketName="flooorplan"
                    filename_label="floor_plan"
                    instructions_title="Drag & drop your floor plan here"
                    accept=".pdf,image/*"
                  />
                </FormField>

                {submitError && <InlineAlert severity="error">{submitError}</InlineAlert>}

                {/* Submit Button */}
                <Box sx={{ pt: 1 }}>
                  <Box
                    component="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    onMouseEnter={() => setSubmitHovered(true)}
                    onMouseLeave={() => setSubmitHovered(false)}
                    sx={{
                      width: '100%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 1.5,
                      backgroundColor: submitting
                        ? 'rgba(33,131,51,0.50)'           // dimmed while in flight
                        : submitHovered ? TOKENS.greenHover : TOKENS.green,
                      color: '#fff', border: 'none',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      px: 4, py: 2,
                      fontSize: '0.875rem', fontWeight: 500, letterSpacing: '0.05em',
                      ...bodyFont,
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit My Details'}
                  </Box>
                </Box>

              </Box>
            )}
          </Box>

        </Box>
      </Box>
      <SectionRule />
    </AnimatedSection>
  );
}

// ─── Homepage ────────────────────────────────────────────────────────────────
export default function Homepage() {

  useStyleTag(KEYFRAMES);

  // Scrolls to the section named in the URL hash (e.g. /#final-cta) on arrival.
  // replaceState cleans the hash from the address bar without triggering a re-render.
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (!hash) return;

    document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    window.history.replaceState(null, '', location.pathname);
  }, []);

  return (
    <Box
      sx={{
        ...bodyFont,
        backgroundColor: '#fff',
        color: TOKENS.charcoal,
      }}
    >
      <Header />
      <HeroSection />
      <MaterialSourcingSection />
      <SamplesSection />
      <PlanningSection />
      <ProfessionalsSection />
      <FinalCTASection />
      <Footer />
    </Box>
  );
}